import { useState } from "react";
import type { IngredientGroup } from "@/lib/types/recipe";
import { formatQuantity } from "@/lib/unitFractionMap";
import { scaleQuantity } from "@/lib/quantity";
import { unitDisplayByKey } from "@/lib/units";
import { IngredientScaler } from "./IngredientScaler";

export type { Ingredient } from "@/lib/types/recipe";

interface IngredientsListProps {
    ingredients: IngredientGroup[];
}

// Scale a quantity string for display. Scalable amounts (single values and simple
// ranges) are multiplied and snapped back to kitchen fractions; anything unscalable
// ("to taste", "1 (15-oz)") passes through unchanged.
const scaledQuantity = (quantity: string | undefined, multiplier: number): string | null => {
    return formatQuantity(scaleQuantity(quantity, multiplier) ?? quantity);
};

export const IngredientsList = ({ ingredients }: IngredientsListProps) => {
    const [multiplier, setMultiplier] = useState(1);

    return (
        <div className="@container">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-2xl @min-[330px]:text-3xl">Ingredients</h2>
                <IngredientScaler value={multiplier} onChange={setMultiplier} />
            </div>
            {ingredients.map((group) => (
                <div key={group.id} className="ml-2 mb-5">
                    {group.title && (
                        <h3 className="text-xl tracking-wide mb-1 pb-1 border-b border-accent-200 inline-block pr-6">
                            {group.title}
                        </h3>
                    )}
                    <ul className="text-lg ml-6 leading-relaxed">
                        {group.ingredients.map((ing) => (
                            <li key={ing.id} className="mb-1">
                                {ing.quantity && <>{scaledQuantity(ing.quantity, multiplier)} </>}
                                {ing.unit && <>{unitDisplayByKey.get(ing.unit) || ing.unit} </>}
                                <span className="font-semibold">{ing.name}</span>
                                {ing.notes && <>, {ing.notes}</>}
                                {ing.optional && <span className="text-sm italic text-default-500"> (optional)</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};
